import { DatePipe } from '@angular/common';
import { Component, Inject, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PartyLink } from 'projects/reg-hub-common/src/lib/interfaces/parties/party-link';
import { eOrderType, ePartySubType, Order, OrderManagerService, OrderRepositoryService, OrderUIConfiguration, Party, PartyRepositoryService, Utilities } from 'reg-hub-common';
import { BehaviorSubject, concat, concatMap, delay, forkJoin, map, mergeMap, tap, timeout, timer } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-copy-parties',
    templateUrl: './copy-parties.component.html',
    styleUrl: './copy-parties.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CopyPartiesComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  protected order!: Order;
  protected number!: string;
  protected partySubType!: ePartySubType;
  protected orderUIConfiguration!: OrderUIConfiguration;
  protected orderFound = false;
  protected parties$ = new BehaviorSubject<Party[]>([]);
  protected loading = true;
  protected saving = false;
  protected saved = false;
  protected error = false;
  protected selectedParties: Party[] = [];

  protected displayedColumns = [
    "select",
    "name",
    "party-type",
    "contactDetails"
  ]

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { number: string, order: Order, partySubType: ePartySubType, orderUIConfiguration: OrderUIConfiguration },
    private orderRepository: OrderRepositoryService,
    private datePipe: DatePipe,
    private partyRepo: PartyRepositoryService,
    private orderManager: OrderManagerService,
    private dialogRef: MatDialogRef<CopyPartiesComponent>) {
    this.number = data.number;
    this.order = data.order;
    this.partySubType = data.partySubType;
    this.orderUIConfiguration = data.orderUIConfiguration;
  }

  ngOnInit(): void {
    this.orderRepository.getOrders({ registrationNumber: this.number })
      .subscribe(orders => {
        this.orderFound = (orders?.length ?? 0) > 0;
        this.loading = false;
        const parties = orders?.at(0)?.parties?.filter(party => {
          if(this.orderUIConfiguration.hasDebtors) {
            return Utilities.isDebtor(party) || Utilities.isSecuredParty(party);
          } else {
            return Utilities.isSecuredParty(party);
          }
        }) ?? [];
        this.parties$.next(parties);
      });
  }

  onSelectionChange(party: Party) {
    if(this.selectedParties.includes(party)) {
      this.selectedParties.splice(this.selectedParties.indexOf(party), 1);
    } else {
      this.selectedParties.push(party);
    }
  }

  onCopySelected() {
    this.saving = true;
    let partiesToSkip: Party[] = [];

    const partyCreations = this.selectedParties.sort((a, b) => (a.linkedParties?.length ?? 0) < (b.linkedParties?.length ?? 0) ? 1 : -1).map(party => {
      if(partiesToSkip.some(p => party.id === p.id)) {
        return;
      }

      if(Utilities.isSecuredParty(party)) {
        party.partySubType = this.partySubType;
      }

      //Need to send linked parties as a party in the party links array, not with the parties array
      if(party.linkedParties) {
        const linkedParties: PartyLink[] = [];

        party.linkedParties.forEach(lp => {
          const partyToLink = this.selectedParties.find(p => p.id === lp.linkedPartyID);

          if(partyToLink) {
            partiesToSkip.push(partyToLink);
          } else {
            return;
          }

          linkedParties.push({
            id: '',
            added: new Date(),
            isNewLink: false,
            partyID: '',
            linkedPartyID: '',
            partyLinkType: lp.partyLinkType,
            linkedParty: partyToLink
          })
        });

        party.linkedParties = linkedParties;
      }

      return this.partyRepo.createParty(`orders/${this.order!.id}/parties`, party)
          .pipe(
              map(result => ((result as any).resource) as Party),
              tap(p => this.order!.parties!.push(p)),
              tap(p => {
                const getParties = p.linkedParties?.map(lp => this.partyRepo.getParty(this.order.id, lp.linkedPartyID)
                    .pipe(tap(lp => this.order!.parties!.push(lp))));
                forkJoin(getParties ?? []).subscribe();
              }))
    }).filter(observable => !!observable);

    forkJoin(partyCreations)
      .subscribe({
        error: () => {
          this.saving = false;
          this.error = true;
        },
        complete: () => {
          this.saving = false;
          this.saved = true;
          this.orderManager.updateOrder(this.order!);
          timer(2000).subscribe(() => this.dialogRef.close());
        }
      })
  }

  getName(party: Party) {
    let name: string | null | undefined;

    if (Utilities.isIndividual(party)) {
      var fullName = this.getIndividualName(party);

      if (party.dateOfBirth) {
        let formattedDateOfBirth = this.datePipe.transform(party.dateOfBirth, 'yyyy-MM-dd');
        fullName += ` (${formattedDateOfBirth})`;
      }

      name = fullName.trim();
    } else {
      name = party.busName?.trim();
    }

    if (name) {
      return name;
    } else {
      var type = Utilities.getPartyRoleLabel(party);

      if (this.order!.orderTypeID === eOrderType.basicDischarge ||
        this.order!.orderTypeID === eOrderType.basicRenewal) {
        return type;
      } else {
        return this.transloco.translate('orders.newPartyType', { type });
      }
    }
  }

  getIndividualName(party: Party): string {
    let fullName = '';

    if (party.middleName == null || party.middleName == "") {
      fullName = party.firstName + " " + party.lastName;
    } else {
      fullName = party.firstName + " " + party.middleName + " " + party.lastName;
    }

    return fullName;
  }

  getPartyTypeString(party: Party) {
    return Utilities.getPartyTypeString(party);
  }

  getAddress(party: Party) {
    const addressFields = [
      party.contactDetails?.address?.unit,
      party.contactDetails?.address?.streetNumber,
      party.contactDetails?.address?.streetName,
      party.contactDetails?.address?.city,
      party.contactDetails?.address?.jurisdiction,
      party.contactDetails?.address?.countryCode,
      party.contactDetails?.address?.postalCode
    ].filter(field => field);

    let result: string = '';
    const isJurisdictionNotNull = !!party.contactDetails?.address?.jurisdiction;

    for (let i = 0; i < addressFields.length; i++) {
      result += addressFields[i];

      if (isJurisdictionNotNull && addressFields[i] === party.contactDetails?.address.jurisdiction) {
        result += ',';
      }

      if (i != addressFields.length - 1) {
        result += ' ';
      }
    }

    result = result.trim();

    if (result.startsWith(',')) {
      result = result.slice(1);
    }

    if (result.endsWith(',')) {
      result = result.slice(0, result.length - 2);
    }

    return result;
  }

  getContactDetails(party: Party) {

    let result: string = this.getAddress(party);

    if (party.contactDetails?.email) {
      if (result) {
        result += '<br>';
      }

      result += this.transloco.translate('orders.emailValue', { value: party.contactDetails.email })
    }

    if (party.contactDetails?.phoneNumber) {
      if (result) {
        result += '<br>';
      }

      result += this.transloco.translate('orders.phoneValue', { value: party.contactDetails.phoneNumber })
    }

    if (party.contactDetails?.faxNumber) {
      if (result) {
        result += '<br>';
      }

      result += this.transloco.translate('orders.faxValue', { value: party.contactDetails.faxNumber })
    }

    return result;
  }
}
